import { useState } from 'react';
import { Star } from 'lucide-react';
import { getRatingLabel } from './RatingLabels';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

const gapClasses = {
  sm: 'gap-0.5',
  md: 'gap-1',
  lg: 'gap-1.5',
};

export default function StarRating({
  value,
  onChange,
  readonly = false,
  size = 'md',
  showLabel = false,
  className = '',
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState<number>(0);
  const isInteractive = !readonly && !!onChange;

  const displayValue = hoverValue || value;
  const label = getRatingLabel(displayValue);

  const handleMouseEnter = (starValue: number) => {
    if (isInteractive) {
      setHoverValue(starValue);
    }
  };

  const handleMouseLeave = () => {
    if (isInteractive) {
      setHoverValue(0);
    }
  };

  const handleClick = (starValue: number) => {
    if (isInteractive && onChange) {
      onChange(starValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, starValue: number) => {
    if (isInteractive && onChange && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onChange(starValue);
    }
  };

  return (
    <div className={`flex items-center ${gapClasses[size]} ${className}`}>
      <div className={`flex ${gapClasses[size]}`}>
        {[1, 2, 3, 4, 5].map((starValue) => {
          const isFilled = starValue <= displayValue;
          const isHovered = isInteractive && hoverValue >= starValue;

          return (
            <button
              key={starValue}
              type="button"
              onClick={() => handleClick(starValue)}
              onMouseEnter={() => handleMouseEnter(starValue)}
              onMouseLeave={handleMouseLeave}
              onKeyDown={(e) => handleKeyDown(e, starValue)}
              disabled={readonly}
              className={`
                ${isInteractive ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-150 ease-in-out
                ${isInteractive && !readonly ? 'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-1 rounded' : ''}
                disabled:cursor-default
              `}
              aria-label={`Rate ${starValue} stars`}
              tabIndex={readonly ? -1 : 0}
            >
              <Star
                className={`
                  ${sizeClasses[size]}
                  transition-colors duration-150
                  ${isFilled || isHovered
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300 fill-transparent'
                  }
                `}
              />
            </button>
          );
        })}
      </div>

      {showLabel && displayValue > 0 && (
        <span className={`
          font-medium text-gray-700
          ${size === 'sm' ? 'text-xs ml-1' : ''}
          ${size === 'md' ? 'text-sm ml-2' : ''}
          ${size === 'lg' ? 'text-base ml-3' : ''}
        `}>
          {label}
        </span>
      )}
    </div>
  );
}
